import Base from '../../Base/Base';
import { useContext, useEffect, useState } from 'react';
import { Button } from '@upmc/ims.component-library';
import Styles from './AIException.module.scss';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import MaterialIcon from '@material/react-material-icon';
import { useFormik } from 'formik';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { UserContext } from 'context/User/UserContext';
import Yup from 'utils/validation/yup-extensions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { SubmitUnsanctionedAIException } from './AIException_api';

const theme = createTheme({
    palette: {
        primary: {
            // Purple and green play nicely together.
            main: '#771b61',
        },
    },
});

/**
 * AI Exception Form
 *
 * @returns rendered component.
 */

const AIExceptionForm = ({
    userSelected,
    setStep,
    setTrackingNumber,
}) => {
    const [formBody, setFormBody] = useState({
        Name: '',
        NTID: '',
        Esignon: '',
        Email: '',
        Department: '',
        Manager: '',
        Requestee_ESOFIID: '',
        Requestor_ESOFIID: '',
        SoftwareName: '',
        Justification: '',
        Confirmation: false
    });

    const validationSchema = Yup.object().shape({
        SoftwareName: Yup.string(
            'Enter the name of the Unsanctioned AI you will use'
        ).required('Unsanctioned AI name is required'),
        Justification: Yup.string(
            'Enter your Justification of Access'
        ).required('Justification of Access is required.'),
        Confirmation: Yup.boolean()
            .oneOf([true], "You must accept the terms and conditions.")
            .required("You must accept the terms and conditions.")
    });

    /**
     * @type {import('context/User/UserContext').ContextUser}
     */
    const contextUser = useContext(UserContext);
    const user =
        contextUser.userInfo.impersonationProfile ??
        contextUser.userInfo.userProfile;

    const [submissionSending, setSubmissionSending] = useState(false);

    useEffect(() => {
        setFormBody({ ...userSelected });
    }, [userSelected]);

    // Only set this once if the user is not selected(meaning we selected 'myself' as the option)
    // and if the formBody is not already set with the NTID. After we select the 'myself' option the user will not change
    if (!userSelected && !formBody?.NTID && user) {
        setFormBody(prevFormBody => ({
            ...prevFormBody,
            Name: user?.Name,
            NTID: user?.NetworkID,
            Esignon: user?.FamiliarESOID,
            Requestee_ESOFIID: user?.ID,
            Requestor_ESOFIID: user?.ID,
            Email: user?.Email,
            Department: user?.Department,
            Manager: user?.Manager,
        }));
    }

    const SubmitAIException = requiredValues => {
        setSubmissionSending(true);
        setFormBody({
            ...formBody,
            Requestor_ESOFIID: user?.ID,
            SoftwareName: requiredValues.SoftwareName,
            Justification: requiredValues.Justification,
            Confirmation: requiredValues.Confirmation,
        });

        // Set local variable because state is not updated yet
        var submitArgs = {
            ...formBody,
            Requestor_ESOFIID: user?.ID,
            SoftwareName: requiredValues.SoftwareName,
            Justification: requiredValues.Justification,
            Confirmation: requiredValues.Confirmation,
        };
        SubmitUnsanctionedAIException(submitArgs).then(resp => {
            if (setTrackingNumber) {
                setTrackingNumber(resp?.data?.payload);
            }
            setStep();
            setSubmissionSending(false);
        });
    };

    const formik = useFormik({
        initialValues: {
            SoftwareName: '',
            Justification: '',
            Confirmation: false
        },
        validationSchema: validationSchema,
        onSubmit: values => {
            SubmitAIException(values);
        },
    });

    return (
        <Base
            title={`Exception for ${userSelected ? 'Other' : 'Self'}`}
            className={Styles['base--default']}
        >
            <formik>
                <form>
                    <StyleAuxiliary>
                        <ThemeProvider theme={theme}>
                            <Grid
                                container
                                direction='row'
                                spacing={1}
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
                                    <TextField
                                        disabled
                                        fullWidth
                                        value={formBody.Name}
                                        id='outlined-name-input'
                                        label='Name'
                                        type='text'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
                                    <TextField
                                        disabled
                                        value={formBody.NTID}
                                        fullWidth
                                        id='outlined-networkID-input'
                                        label='Network ID'
                                        type='text'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
                                    <TextField
                                        disabled
                                        value={formBody.Esignon}
                                        fullWidth
                                        id='outlined-esignon-input'
                                        label='ESignOn'
                                        type='text'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
                                    <TextField
                                        disabled
                                        value={formBody.Email}
                                        fullWidth
                                        id='outlined-email-input'
                                        label='Email'
                                        type='text'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
                                    <TextField
                                        disabled
                                        value={formBody.Department}
                                        fullWidth
                                        id='outlined-department-input'
                                        label='Department'
                                        type='text'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
                                    <TextField
                                        disabled
                                        value={formBody.Manager}
                                        fullWidth
                                        id='outlined-manager-input'
                                        label='Manager'
                                        type='text'
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                    xl={12}
                                >
                                    <TextField
                                        value={formik.values.SoftwareName}
                                        onChange={formik.handleChange}
                                        name='SoftwareName'
                                        error={
                                            formik.touched.SoftwareName &&
                                            formik.values.SoftwareName.length < 1
                                        }
                                        helperText={
                                            formik.touched.SoftwareName &&
                                            formik.errors.SoftwareName
                                        }
                                        fullWidth
                                        id='outlined-clientsoftware-input'
                                        label='Name of Unsanctioned AI'
                                        type='text'
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                    xl={12}
                                >
                                    <TextField
                                        value={formik.values.Justification}
                                        onChange={formik.handleChange}
                                        name='Justification'
                                        error={
                                            formik.touched.Justification &&
                                            formik.values.Justification.length < 1
                                        }
                                        helperText={
                                            formik.touched.Justification &&
                                            formik.errors.Justification
                                        }
                                        fullWidth
                                        multiline
                                        rows={4}
                                        id='outlined-Justification-input'
                                        label='Justification of Access'
                                        type='text'
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                    xl={12}
                                    alignItems="flex-start"
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formik.values.Confirmation}
                                                onChange={formik.handleChange}
                                                name='Confirmation'
                                                helperText={
                                                    formik.touched.Confirmation &&
                                                    Boolean(formik.errors.Confirmation)
                                                }
                                                fullWidth
                                                id='outlined-usage-terms-toggle'
                                            />
                                        }
                                        label={
                                            <Typography>
                                                I consent that for all Unsanctioned AI Requests, by myself and on behalf of others, will not include the following:
                                                <i>Protected Health Information (PHI), Personally Identifiable Information (PII) for employees or non-employees, or other
                                                    confidential and/or proprietary data (i.e. account names and passwords, social security numbers, credit cards, contracts,
                                                    UPMC financial data, Human Resources confidential data, defense and national security threats data, Homeland Security data,
                                                    bioterrorism data, etc.</i>
                                            </Typography>
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='column'
                                spacing={3}
                                sx={{ alignItems: 'center' }}
                            >
                                <Grid item></Grid>
                                <Grid item>
                                    <Button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            formik.handleSubmit();
                                        }}
                                        type='submit'
                                        startIcon={
                                            submissionSending ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                <MaterialIcon icon='save' />
                                            )
                                        }
                                        variant='contained'
                                        disabled={!formik.values.Confirmation || submissionSending}
                                        sx={{ margin: '5px' }}
                                        id='SubmitAIException'
                                    >
                                        {`${submissionSending
                                            ? 'Submitting'
                                            : 'Submit'
                                            }`}
                                    </Button>
                                </Grid>
                            </Grid>
                        </ThemeProvider>
                    </StyleAuxiliary>
                </form>
            </formik>
        </Base>
    );
};

export default AIExceptionForm;
